import { getContentstackImageURL } from '@noths/polaris-client-utils';
import dynamic from 'next/dynamic';

import type { HeroCarouselSlideProps } from 'src/components/organisms/HeroCarousel/HeroCarousel';
import type { HeroCarouselSectionProps } from 'src/components/sections/HeroCarouselSection/HeroCarouselSection';
import type { ValidationError } from 'src/content/types';
import type { SectionRenderer } from 'src/types/Section';
import type { Transformer } from 'src/types/Transformer';
import type { HeroCarouselItemReference, HeroCarouselSectionContent } from './types';
import { validateHeroCarouselSlideTerms } from './validation';

const SMALL_SCREEN_WIDTH = 768;
const LARGE_SCREEN_WIDTH = 1440;

/** `{ key: value }`, or nothing at all when the value is absent. See the call sites for why. */
const omitWhenUndefined = <Key extends string, Value>(key: Key, value: Value | undefined) =>
  (value === undefined ? {} : { [key]: value }) as { [K in Key]?: Value };

/*
 * Serve resized, webp-optimized images rather than the raw Contentstack originals (which can be many
 * MB and would wreck LCP for this LCP element).
 *
 * Which crop, if any, depends on what the entry has. With a dedicated large-screen asset both images
 * are already cropped by the editor, so imposing a crop here would override their choice. Entries
 * published before that field existed have a single asset uploaded against the old 1440 x 480
 * minimum, so we keep deriving both crops from it exactly as before and they render unchanged.
 */
const getSlideImages = (slide: HeroCarouselItemReference) => {
  const largeScreenAssetURL = slide.image_large_screen?.url;

  if (!largeScreenAssetURL) {
    return {
      imageLargeScreen: getContentstackImageURL(slide.image.url, LARGE_SCREEN_WIDTH, {
        aspectRatio: '3:1',
      }),
      imageSmallScreen: getContentstackImageURL(slide.image.url, SMALL_SCREEN_WIDTH, {
        aspectRatio: '3:4',
      }),
    };
  }

  return {
    imageLargeScreen: getContentstackImageURL(largeScreenAssetURL, LARGE_SCREEN_WIDTH),
    imageSmallScreen: getContentstackImageURL(slide.image.url, SMALL_SCREEN_WIDTH),
  };
};

/* istanbul ignore next */
export const renderer: SectionRenderer<HeroCarouselSectionProps> =
  dynamic<HeroCarouselSectionProps>(() =>
    import('src/components/sections/HeroCarouselSection/HeroCarouselSection').then(
      (mod) => mod.HeroCarouselSection,
    ),
  );

export const transformer: Transformer<HeroCarouselSectionContent, HeroCarouselSectionProps> = (
  { _metadata, items, tracking_title },
  { positionMetadata },
) => {
  const errors: ValidationError[] = [];
  const slides: HeroCarouselSlideProps[] = [];

  (items || []).forEach((item) => {
    const slide = item.hero_carousel_item?.[0];

    if (!slide) {
      return;
    }

    const buttonHref = slide.button_reference?.[0]?.url;

    if (!buttonHref) {
      errors.push({
        message: `Missing referenced page for hero carousel slide (uid: ${slide.uid}). Check that the linked page is published.`,
      });

      return;
    }

    errors.push(...validateHeroCarouselSlideTerms(slide));

    slides.push({
      alt: slide.image.description || slide.heading_line_1,
      buttonHref,
      buttonText: slide.button_text || '',
      headingLine1: slide.heading_line_1,
      headingPosition: slide.heading_position,
      ...getSlideImages(slide),
      paragraph: slide.paragraph || '',
      /*
       * These props are serialized into the page payload by getServerSideProps, which rejects
       * `undefined` outright, so an absent optional field has to omit its key rather than set it to
       * undefined. All three fields are optional in Contentstack and any of them can be missing.
       */
      ...omitWhenUndefined('headingLine2', slide.heading_line_2),
      ...omitWhenUndefined('termsAndConditions', slide.terms_and_conditions),
      ...omitWhenUndefined(
        'termsAndConditionsHref',
        slide.terms_and_conditions_page_reference?.[0]?.url,
      ),
    });
  });

  return {
    uid: _metadata.uid,
    props: {
      // Drives the top spacing: nothing above us to space off the header when we lead the page.
      isFirstSection: positionMetadata.position === 0,
      items: slides,
      trackingTitle: tracking_title,
    },
    errors,
  };
};
